import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { SafeStyle } from '@angular/platform-browser';
import {
  atxMatchingDeviceSize$,
  ATX_DEVICE_SIZE,
} from '@epicuro-next/ataraxia/breakpoint';
import { isNonNullish } from '@epicuro-next/utilities';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { getResponsiveInput } from './responsive-input-decorator';

const sortedPrefixMap = {
  [ATX_DEVICE_SIZE.XS]: undefined,
  [ATX_DEVICE_SIZE.SM]: 'sm',
  [ATX_DEVICE_SIZE.MD]: 'md',
  [ATX_DEVICE_SIZE.LG]: 'lg',
  [ATX_DEVICE_SIZE.XL]: 'xl',
};

type AttributeValue = string | number | boolean | null;
type Meta = ReturnType<typeof getResponsiveInput>;
@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export abstract class AbstractContentComponent implements OnInit, OnDestroy {
  public styleWithVariables: SafeStyle;
  protected destroy$$ = new Subject<void>();

  constructor(
    protected cdr: ChangeDetectorRef,
    protected elementRef: ElementRef,
    protected zone: NgZone,
  ) {}

  public ngOnInit() {
    this.setCssVariablesFromInputs();
    this.setResponsiveProperties();
  }

  public ngOnDestroy() {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  private setResponsiveProperties() {
    this.zone.runOutsideAngular(() => {
      this.propertyMetas.forEach(({ property, meta }) => {
        this.setPropertyValueFromAttributes(property, meta);
      });
    });
  }

  private setCssVariablesFromInputs() {
    this.zone.runOutsideAngular(() => {
      this.propertyMetas
        .filter(({ meta }) => meta && !!meta.cssVariable)
        .forEach(({ property, meta }) => {
          let prevValue: string | null = meta.defaultValue ?? null;
          Object.values(sortedPrefixMap).forEach((prefix) => {
            const { key, value } = this.getPairValues(property, prefix);

            this.setCssVariable(
              key,
              this.getValueWithMapper(meta, value ?? prevValue),
            );
            prevValue = value ?? prevValue;
          });
        });
    });
  }

  private setPropertyValueFromAttributes(property: string, meta: Meta) {
    this.zone.runOutsideAngular(() => {
      const { self } = this;
      atxMatchingDeviceSize$
        .pipe(takeUntil(this.destroy$$))
        .subscribe((deviceSize) => {
          let result: string | null = self[property];
          const matchingDeviceIndex =
            Object.keys(sortedPrefixMap).indexOf(deviceSize);

          Object.values(sortedPrefixMap).forEach((prefix, index) => {
            if (index > matchingDeviceIndex) return;
            const { value } = this.getPairValues(property, prefix);
            result = value ?? result;
          });

          const mappedValue = this.getValueWithMapper(meta, result);
          const calculatedVal = this.getTypeSafeCalculatedAttr(
            mappedValue,
            meta,
          );

          this.changePropertyWithNgOnChanges(
            property,
            self[property],
            calculatedVal,
          );
        });
    });
  }

  private changePropertyWithNgOnChanges(
    property: string,
    previousValue: AttributeValue,
    currentValue: AttributeValue,
  ) {
    if (previousValue === currentValue) return;

    const { self } = this;

    self[property] = currentValue;

    const { ngOnChanges } = self;
    if (ngOnChanges) {
      ngOnChanges.bind(self)({
        [property]: {
          firstChange: false,
          isFirstChange: () => false,
          previousValue,
          currentValue,
        },
      } as SimpleChanges);
    }
    this.cdr.detectChanges();
  }

  private getTypeSafeCalculatedAttr(
    value: AttributeValue,
    meta: Meta,
  ): AttributeValue {
    if (meta.isBoolean) {
      if (value === 'false') return false;
      if (value === '' || !!value) return true;
      return false;
    }
    if (meta.isNumber) {
      return parseInt(value as string, 10);
    }

    return value;
  }

  private getPairValues(property: string, prefix?: string) {
    const key = attributeKey(property, prefix);
    const value = this.attributeValue(key);

    return { key, value };
  }

  private getValueWithMapper(meta: Meta, value: string | null) {
    if (!value) return value;

    const map = meta.predefinedMap;
    const mappedValue = map ? map.get(value) : undefined;

    return mappedValue ?? value;
  }

  private get propertyMetas() {
    const proto = Object.getPrototypeOf(this.self);
    return Object.getOwnPropertyNames(proto)
      .map((property) => ({
        property,
        meta: getResponsiveInput(this.self, property),
      }))
      .filter(isNonNullish)
      .filter(({ meta }) => isNonNullish(meta));
  }

  private get host(): HTMLElement | undefined {
    return this.elementRef?.nativeElement as HTMLElement;
  }

  private get self() {
    // tslint:disable-next-line:no-any
    return this as any;
  }

  private attributeValue(key: string) {
    return this.host?.getAttribute(key);
  }

  private setCssVariable(key: string, value: string | null) {
    if (!value || !this.host) return;
    this.host.style.setProperty(`--${key}`, value);
    this.cdr.markForCheck();
  }
}

function attributeKey(property: string, prefix?: string) {
  return prefix
    ? `${prefix.toLowerCase()}-${pascalToKebabCase(property)}`
    : pascalToKebabCase(property);
}

function pascalToKebabCase(str: string) {
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/\s+/g, '-')
    .toLowerCase();
}
